import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
type Attempt = {
  id: number; alert_type: string; origin: string; status: string;
  predicted_segments: number; charged_segments: number; reported_segments: number | null;
  price: string | null; currency: string | null; reconciled_at: string | null;
  provider_status: string | null; provider_sid: string | null; created_at: string;
  daily_window_start?: string | null; daily_charged?: boolean;
};
export default function AdminSmsAttempts({ userId }: { userId?: string }) {
  const queryClient = useQueryClient();
  const [notice, setNotice] = useState("");
  const [refreshing, setRefreshing] = useState(false);
  const key = `/api/admin/sms-attempts${userId ? `?userId=${encodeURIComponent(userId)}` : ""}`;
  const { data, isError, isLoading } = useQuery<Attempt[]>({ queryKey: [key], refetchInterval: 60_000 });
  const reconcile = async () => {
    setRefreshing(true);
    try {
      const res = await fetch("/api/admin/sms-reconcile", { method: "POST", credentials: "include" });
      const result = await res.json();
      setNotice(result.message);
      if (res.ok) await queryClient.invalidateQueries({ queryKey: [key] });
    } catch { setNotice("Provider metadata is unavailable. No messages were sent."); }
    finally { setRefreshing(false); }
  };
  return <Card className="my-6">
    <CardHeader><CardTitle>Automated SMS accounting</CardTitle></CardHeader>
    <CardContent className="space-y-3">
      <p className="text-xs text-muted-foreground">Last 100 attempts. Shared limits: 3 alert texts per home day (12:01 a.m. reset), once per matching alert per day, 372 segments per UTC month, maximum 4 segments per SMS. Admin tests count. Pending prices are not zero. Unknown submissions remain counted and are never automatically retried.</p>
      <Button variant="outline" disabled={refreshing} onClick={reconcile}>{refreshing ? "Checking..." : "Refresh provider metadata (no sends)"}</Button>
      {notice && <p className="text-xs" role="status">{notice}</p>}
      {isLoading ? <p>Loading records...</p> : isError ? <p role="alert">SMS records unavailable.</p> :
        <div className="overflow-x-auto"><table className="text-xs w-full text-left">
          <thead><tr>{["Type / origin", "State", "Predicted / counted / reported", "Provider charge", "Reconciliation"].map(h => <th key={h} className="p-2">{h}</th>)}</tr></thead>
          <tbody>{data?.map(a => <tr key={a.id} className="border-t">
            <td className="p-2">{a.alert_type} / {a.origin}<br />{new Date(a.created_at).toLocaleString()}</td>
            <td className="p-2">{a.status}<br />{a.provider_status ?? "No provider status"}<br />Daily slot: {a.daily_charged ? "Counted" : "Not counted"}</td>
            <td className="p-2">{a.predicted_segments} / {a.charged_segments} / {a.reported_segments ?? "pending"}</td>
            <td className="p-2">{a.price == null || a.currency == null ? "pending" : `${Math.abs(Number(a.price)).toFixed(5)} ${a.currency.toUpperCase()}`}</td>
            <td className="p-2">{a.reconciled_at ? "Complete" : a.provider_sid ? "Pending provider fields" : a.status === "uncertain" || a.status === "reserved" ? "Unknown submission; counted" : "Not submitted"}
              {a.provider_sid && <details><summary>Provider ID</summary>{a.provider_sid}</details>}
            </td>
          </tr>)}</tbody>
        </table>{!data?.length && <p className="py-3">No attempts recorded.</p>}</div>}
      <p className="text-xs text-muted-foreground">Message charges only. International rates, carrier changes, taxes, number/campaign charges, AI and payment fees are separate. Cost savings are estimates until an approved delivered report has populated provider segments and price.</p>
    </CardContent>
  </Card>;
}
